<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="680px"
    top="5vh"
    @close="handleClose"
    class="summary-modal"
  >
    <div class="summary-container">
      <!-- Header -->
      <div class="summary-header">
        <h2 class="campaign-title">{{ campaignTitle }}</h2>
        <div class="header-meta">
          <span :class="`status-badge status-${campaignStatus}`">{{ formatStatus(campaignStatus) }}</span>
          <span class="meta-divider" v-if="periodLabel">·</span>
          <span class="meta-text" v-if="periodLabel">{{ periodLabel }}</span>
          <span class="meta-divider" v-if="typeLabel">·</span>
          <span class="meta-text" v-if="typeLabel">{{ typeLabel }}</span>
          <span class="meta-divider" v-if="rewardTypeLabel">·</span>
          <span class="meta-text" v-if="rewardTypeLabel">{{ rewardTypeLabel }}</span>
        </div>

        <!-- Duration Progress Bar -->
        <div class="progress-section">
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: durationProgress + '%' }"></div>
          </div>
          <div class="progress-label">{{ durationLabel }}</div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <loading-even></loading-even>
      </div>

      <!-- Content -->
      <div v-else class="summary-content thin-scrollbar">
        <!-- Empty State -->
        <div v-if="participantCount === 0 && !isUpcoming" class="empty-state">
          <div class="empty-icon">📊</div>
          <div class="empty-text">No data yet</div>
          <div class="empty-subtext">Summary will appear once participants join the campaign</div>
        </div>

        <template v-else>
          <!-- Section 1: Investment & Impact -->
          <div class="section">
            <div class="section-title">Investment & Impact</div>

            <!-- Hero: Coins Invested -->
            <div class="investment-hero">
              <div class="investment-hero-top">
                <div class="investment-amount">
                  <div class="coin-inline relative" style="width: 22px; height: 22px;"></div>
                  {{ formatValue(totalCoinsOut) }}
                </div>
                <div class="investment-label">{{ coinsOutLabel }}</div>
              </div>
              <div class="investment-meta">
                {{ formatValue(costPerActive) }} per active participant
                <span v-if="hasDailyGoalData"> · via {{ formatValue(totalDailyGoalsAchieved) }} daily goals</span>
              </div>

              <!-- Coin flow detail -->
              <div class="coin-flow">
                <div class="coin-flow-row">
                  <span class="coin-flow-label">Entry fees collected</span>
                  <span class="coin-flow-value">
                    <template v-if="entryCost > 0">
                      <div class="coin-inline relative" style="width: 13px; height: 13px;"></div>
                      {{ formatValue(totalCoinsIn) }}
                      <span class="coin-flow-detail">({{ formatValue(signupCount) }} × {{ formatValue(entryCost) }})</span>
                    </template>
                    <template v-else>
                      Free entry
                    </template>
                  </span>
                </div>
                <!-- Instant: coins are generated and immediately distributed -->
                <div class="coin-flow-row" v-if="rewardType === 'instant' && hasDailyGoalData">
                  <span class="coin-flow-label">Coins distributed</span>
                  <span class="coin-flow-value">
                    <div class="coin-inline relative" style="width: 13px; height: 13px;"></div>
                    {{ formatValue(totalSystemCoinsGenerated) }}
                  </span>
                </div>
                <!-- Charged: coins are committed to escrow, then earned or returned -->
                <template v-if="rewardType === 'charged' && hasDailyGoalData">
                  <div class="coin-flow-row">
                    <span class="coin-flow-label">Coins committed</span>
                    <span class="coin-flow-value">
                      <div class="coin-inline relative" style="width: 13px; height: 13px;"></div>
                      {{ formatValue(totalSystemCoinsGenerated) }}
                      <span class="coin-flow-detail" v-if="campaignStatus === 'active'">(in escrow)</span>
                    </span>
                  </div>
                  <div class="coin-flow-row" v-if="campaignStatus !== 'active' && forfeitedCoins > 0">
                    <span class="coin-flow-label">Returned to system</span>
                    <span class="coin-flow-value coin-flow-returned">
                      <div class="coin-inline relative" style="width: 13px; height: 13px;"></div>
                      -{{ formatValue(forfeitedCoins) }}
                      <span class="coin-flow-detail">({{ formatValue(forfeitedCount) }} didn't meet goal)</span>
                    </span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Impact cards: 2-column -->
            <div class="impact-grid">
              <div class="impact-card">
                <div class="impact-value impact-value-good">
                  {{ achieversCount }} / {{ activeParticipants }}
                </div>
                <div class="impact-label">Achieved Goal</div>
              </div>
              <div class="impact-card" v-if="hasDailyGoalData">
                <div class="impact-value">{{ formatValue(totalDailyGoalsAchieved) }}</div>
                <div class="impact-label">Daily Goals Hit</div>
              </div>
              <div class="impact-card" v-else>
                <div class="impact-value">{{ formatValue(totalActions) }}</div>
                <div class="impact-label">Total Actions</div>
              </div>
            </div>

            <!-- Projection notice for active campaigns -->
            <div class="projection-notice" v-if="campaignStatus === 'active' && durationProgress < 10">
              Limited data — projections will improve as the campaign progresses
            </div>
            <div class="projection-row" v-if="campaignStatus === 'active' && durationProgress >= 10">
              <span class="projection-label">Projected total investment:</span>
              <span class="projection-value">
                <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                {{ formatValue(projectedCoinsOut) }}
              </span>
            </div>
          </div>

          <!-- Section 2: Participation -->
          <div class="section">
            <div class="section-title">Participation</div>
            <div class="participation-bar">
              <div class="participation-stats">
                <div class="part-stat">
                  <span class="part-stat-value">{{ formatValue(signupCount) }}</span>
                  <span class="part-stat-label">Signed Up</span>
                </div>
                <div class="part-stat-divider"></div>
                <div class="part-stat">
                  <span class="part-stat-value">{{ formatValue(activeParticipants) }}</span>
                  <span class="part-stat-label">Active ({{ participationRate }}%)</span>
                </div>
                <div class="part-stat-divider"></div>
                <div class="part-stat">
                  <span class="part-stat-value">{{ formatValue(achieversCount) }}</span>
                  <span class="part-stat-label">Achieved Goal</span>
                </div>
              </div>
              <div class="achievement-progress">
                <div class="achievement-bar-container">
                  <div class="achievement-bar-fill" :style="{ width: achievementRate + '%' }"></div>
                </div>
                <div class="achievement-label">{{ achievementRate }}% achievement rate</div>
              </div>
            </div>
          </div>

          <!-- Section 3: Reward Breakdown -->
          <div class="section">
            <div class="section-title">Reward Breakdown</div>

            <!-- Instant Coins -->
            <div class="reward-details" v-if="rewardType === 'instant'">
              <div class="reward-row">
                <span class="reward-row-label">Coins per daily goal</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(rewardCoins) }}
                </span>
              </div>
              <div class="reward-row" v-if="dailyTarget">
                <span class="reward-row-label">Daily target</span>
                <span class="reward-row-value">{{ formatValue(dailyTarget) }}</span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Daily goals achieved</span>
                <span class="reward-row-value">{{ formatValue(totalDailyGoalsAchieved) }}</span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Total actions recorded</span>
                <span class="reward-row-value">{{ formatValue(totalActions) }}</span>
              </div>
              <div class="reward-row reward-row-total">
                <span class="reward-row-label">Total coins invested</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(totalSystemCoinsGenerated) }}
                </span>
              </div>
            </div>

            <!-- Charged Coins -->
            <div class="reward-details" v-else-if="rewardType === 'charged'">
              <div class="reward-row">
                <span class="reward-row-label">Coins per daily goal</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(chargedCoinsPerAction) }}
                </span>
              </div>
              <div class="reward-row" v-if="dailyTarget">
                <span class="reward-row-label">Daily target</span>
                <span class="reward-row-value">{{ formatValue(dailyTarget) }}</span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Daily goals achieved</span>
                <span class="reward-row-value">{{ formatValue(totalDailyGoalsAchieved) }}</span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Total coins committed</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(totalSystemCoinsGenerated) }}
                </span>
              </div>
              <div class="reward-row reward-row-total" v-if="campaignStatus !== 'active'">
                <span class="reward-row-label">Earned by {{ formatValue(achieversCount) }} who met goal</span>
                <span class="reward-row-value reward-positive">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(eligibleCoins) }}
                </span>
              </div>
              <div class="reward-row" v-if="campaignStatus !== 'active' && forfeitedCoins > 0">
                <span class="reward-row-label">Returned from {{ formatValue(forfeitedCount) }} who didn't</span>
                <span class="reward-row-value reward-neutral">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(forfeitedCoins) }}
                </span>
              </div>
              <div class="reward-row" v-if="campaignStatus === 'active'">
                <span class="reward-row-label">Status</span>
                <span class="reward-row-value reward-neutral">In escrow until campaign ends</span>
              </div>
            </div>

            <!-- Prize Pool -->
            <div class="reward-details" v-else-if="rewardType === 'prize_pool'">
              <div class="reward-row">
                <span class="reward-row-label">Entry fees collected</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(totalCoinsIn) }}
                </span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Total pool size</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(prizePoolSize) }}
                </span>
              </div>
              <div class="reward-row">
                <span class="reward-row-label">Current winners</span>
                <span class="reward-row-value">{{ formatValue(achieversCount) }}</span>
              </div>
              <div class="reward-row reward-row-total">
                <span class="reward-row-label">Prize per winner</span>
                <span class="reward-row-value">
                  <template v-if="achieversCount > 0">
                    <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                    {{ formatValue(prizePerWinner) }}
                  </template>
                  <template v-else>No winners yet</template>
                </span>
              </div>
            </div>

            <!-- Unknown reward type fallback -->
            <div class="reward-details" v-else>
              <div class="reward-row">
                <span class="reward-row-label">Total coins invested</span>
                <span class="reward-row-value">
                  <div class="coin-inline relative" style="width: 14px; height: 14px;"></div>
                  {{ formatValue(totalCoinsOut) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Section 4: Campaign Results (ended campaigns only) -->
          <div class="section" v-if="campaignStatus === 'ended'">
            <div class="section-title">Campaign Results</div>
            <div class="effectiveness-grid">
              <div class="eff-stat">
                <span class="eff-stat-value" :class="achievementRateColor">{{ achievementRate }}%</span>
                <span class="eff-stat-label">Achievement Rate</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ formatValue(avgScore) }} / {{ formatValue(individualTarget) }}</span>
                <span class="eff-stat-label">Avg Score</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ participationRate }}%</span>
                <span class="eff-stat-label">Participation Rate</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ dropOffRate }}%</span>
                <span class="eff-stat-label">Drop-off</span>
              </div>
            </div>
          </div>

          <!-- Section 4 for active campaigns: Progress -->
          <div class="section" v-if="campaignStatus === 'active'">
            <div class="section-title">Progress</div>
            <div class="effectiveness-grid">
              <div class="eff-stat">
                <span class="eff-stat-value" :class="achievementRateColor">{{ achievementRate }}%</span>
                <span class="eff-stat-label">Achievement Rate</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ formatValue(avgScore) }}</span>
                <span class="eff-stat-label">Avg Score</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ participationRate }}%</span>
                <span class="eff-stat-label">Participation Rate</span>
              </div>
              <div class="eff-stat">
                <span class="eff-stat-value">{{ dropOffRate }}%</span>
                <span class="eff-stat-label">Drop-off</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="summary-footer">
        <span class="footer-link" @click="openLeaderboard">View Leaderboard</span>
        <el-button size="small" @click="dialogVisible = false">Close</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "CampaignSummaryModal",
  components: {
    loadingEven: require("../even/loading").default,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    campaign: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      signups: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.$emit("close");
        }
      },
    },
    campaignTitle() {
      if (!this.campaign) return "";
      const title = this.campaign.title;
      if (!title) return "";
      if (typeof title === "string") return title;
      if (typeof title === "object") {
        return title.en || title.th || Object.values(title).find(v => v) || "";
      }
      return "";
    },
    campaignStatus() {
      if (!this.campaign) return "";
      if (this.campaign.status === "archived") return "archived";
      if (!this.campaign.timing) return "active";

      const now = new Date();
      const startDate = this.parseDate(this.campaign.timing.startDate);
      const endDate = this.parseDate(this.campaign.timing.endDate);

      if (!startDate) return "active";
      if (now < startDate) return "upcoming";
      if (endDate && now > endDate) return "ended";
      return "active";
    },
    isUpcoming() {
      return this.campaignStatus === "upcoming";
    },
    periodLabel() {
      if (!this.campaign || !this.campaign.timing) return "";
      const timing = this.campaign.timing;
      if (!timing.startDate || !timing.endDate) return "";
      const start = this.parseDate(timing.startDate);
      const end = this.parseDate(timing.endDate);
      if (!start || !end) return "";
      return this.formatDateShort(start) + " - " + this.formatDateShort(end);
    },
    typeLabel() {
      if (!this.campaign || !this.campaign.inputType) return "";
      const types = {
        quiz: "Quiz",
        count: "Count",
        auto_sync: "Auto Sync",
        auto_sync_steps: "Steps",
        auto_sync_sleep: "Sleep",
        auto_sync_screen: "Screen Time",
        auto_sync_exercise: "Exercise",
        auto_sync_distance: "Distance",
        multiple_selection: "Multiple Selection",
        yes_no: "Yes/No",
      };
      return types[this.campaign.inputType] || this.campaign.inputType;
    },
    rewardTypeLabel() {
      if (!this.rewardConfig) return "";
      const types = {
        instant: "Instant Coins",
        charged: "Charged Coins",
        prize_pool: "Prize Pool",
      };
      return types[this.rewardConfig.type] || this.rewardConfig.type;
    },
    rewardConfig() {
      if (!this.campaign || !this.campaign.reward) return null;
      return this.campaign.reward;
    },
    rewardType() {
      return this.rewardConfig ? this.rewardConfig.type : null;
    },
    rewardCoins() {
      if (!this.rewardConfig) return 0;
      return this.rewardConfig.coins || 0;
    },
    chargedCoinsPerAction() {
      if (!this.rewardConfig) return 0;
      return this.rewardConfig.chargedCoins || 0;
    },
    summary() {
      if (!this.campaign || !this.campaign.id) return null;
      return this.$store.getters["happilyvillageUniversity/getCampaignSummary"](this.campaign.id);
    },
    leaderboard() {
      if (!this.summary || !this.summary.leaderboard) return [];
      return [...this.summary.leaderboard].sort((a, b) => (a.rank || 999) - (b.rank || 999));
    },
    individualTarget() {
      if (!this.campaign || !this.campaign.goals) return 0;
      return this.campaign.goals.individualTarget || 0;
    },
    entryCost() {
      if (!this.campaign) return 0;
      return this.campaign.entryCost || 0;
    },
    signupCount() {
      // For upcoming campaigns use signups list length, otherwise leaderboard or totalParticipants
      if (this.isUpcoming) return this.signups.length;
      if (this.campaign && this.campaign.totalParticipants) return this.campaign.totalParticipants;
      return this.leaderboard.length;
    },
    participantCount() {
      return this.leaderboard.length;
    },
    activeParticipants() {
      return this.leaderboard.filter(p => (p.value || 0) > 0).length;
    },
    achieversCount() {
      if (!this.individualTarget) return 0;
      return this.leaderboard.filter(p => this.hasAchievedGoal(p)).length;
    },
    achievementRate() {
      if (this.activeParticipants === 0) return 0;
      return Math.round((this.achieversCount / this.activeParticipants) * 100);
    },
    participationRate() {
      const total = this.signupCount || this.participantCount;
      if (total === 0) return 0;
      return Math.round((this.activeParticipants / total) * 100);
    },
    dropOffRate() {
      const total = this.signupCount || this.participantCount;
      if (total === 0) return 0;
      return Math.round(((total - this.activeParticipants) / total) * 100);
    },

    // Economy
    totalCoinsIn() {
      return this.entryCost * this.signupCount;
    },
    totalCoinsOut() {
      // For charged campaigns that have ended, only achiever coins were actually distributed.
      // During active campaigns, use total committed (all coins are still in escrow).
      if (this.rewardType === "charged") {
        if (this.campaignStatus === "active" && this.totalSystemCoinsGenerated > 0) {
          return this.totalSystemCoinsGenerated;
        }
        // Ended/archived: use eligible coins (only achievers earned)
        if (this.eligibleCoins > 0) {
          return this.eligibleCoins;
        }
      }
      // For instant/prize_pool, try leaderboard coin fields first
      const fromLeaderboard = this.leaderboard.reduce((sum, p) => sum + this.getCoinsEarned(p), 0);
      if (fromLeaderboard === 0 && this.totalSystemCoinsGenerated > 0) {
        return this.totalSystemCoinsGenerated;
      }
      return fromLeaderboard;
    },
    coinsOutLabel() {
      if (this.rewardType === "charged" && this.campaignStatus === "active") {
        return "coins committed to behavior change";
      }
      return "coins earned through behavior change";
    },
    costPerActive() {
      if (this.activeParticipants === 0) return 0;
      return Math.round(this.totalCoinsOut / this.activeParticipants);
    },
    projectedCoinsOut() {
      if (this.durationProgress <= 0) return this.totalCoinsOut;
      return Math.round((this.totalCoinsOut / this.durationProgress) * 100);
    },
    // Daily goal metrics
    dailyTarget() {
      if (!this.campaign || !this.campaign.goals) return 0;
      return this.campaign.goals.dailyTarget || 0;
    },
    totalDailyGoalsAchieved() {
      // Derive from each participant's cumulative value and the daily target.
      // Math.floor(value / dailyTarget) gives the number of daily-target-equivalent
      // completions each participant accumulated.
      if (!this.dailyTarget || (this.rewardType !== "instant" && this.rewardType !== "charged")) return 0;
      return this.leaderboard.reduce((sum, p) => {
        const value = p.value || 0;
        return sum + Math.floor(value / this.dailyTarget);
      }, 0);
    },
    totalSystemCoinsGenerated() {
      // Total coins the system created for daily goal rewards.
      // = dailyGoalsAchieved × coins per daily goal
      if (this.rewardType === "instant") {
        return this.totalDailyGoalsAchieved * this.rewardCoins;
      }
      if (this.rewardType === "charged") {
        return this.totalDailyGoalsAchieved * this.chargedCoinsPerAction;
      }
      return 0;
    },
    hasDailyGoalData() {
      return (this.rewardType === "instant" || this.rewardType === "charged") && this.dailyTarget > 0;
    },

    // Reward breakdown helpers
    totalActions() {
      return this.leaderboard.reduce((sum, p) => sum + (p.value || 0), 0);
    },
    totalEscrow() {
      // Try leaderboard field first, fall back to calculated system coins
      const fromFields = this.leaderboard.reduce((sum, p) => sum + (p.charged_coins || 0), 0);
      if (fromFields > 0) return fromFields;
      return this.totalSystemCoinsGenerated;
    },
    eligibleCoins() {
      // Try leaderboard fields first, fall back to calculated from value/dailyTarget
      const fromFields = this.leaderboard
        .filter(p => this.hasAchievedGoal(p))
        .reduce((sum, p) => sum + (p.charged_coins || p.total_coins || 0), 0);
      if (fromFields > 0) return fromFields;
      // Calculate: each achiever's daily goals × chargedCoinsPerAction
      const coinsPerGoal = this.chargedCoinsPerAction || this.rewardCoins || 0;
      if (!this.dailyTarget || !coinsPerGoal) return 0;
      return this.leaderboard
        .filter(p => this.hasAchievedGoal(p))
        .reduce((sum, p) => sum + (Math.floor((p.value || 0) / this.dailyTarget) * coinsPerGoal), 0);
    },
    forfeitedCount() {
      if (!this.individualTarget) return 0;
      return this.leaderboard.filter(p => (p.value || 0) > 0 && !this.hasAchievedGoal(p)).length;
    },
    forfeitedCoins() {
      // Try leaderboard fields first, fall back to calculated from value/dailyTarget
      const fromFields = this.leaderboard
        .filter(p => (p.value || 0) > 0 && !this.hasAchievedGoal(p))
        .reduce((sum, p) => sum + (p.charged_coins || 0), 0);
      if (fromFields > 0) return fromFields;
      const coinsPerGoal = this.chargedCoinsPerAction || this.rewardCoins || 0;
      if (!this.dailyTarget || !coinsPerGoal) return 0;
      return this.leaderboard
        .filter(p => (p.value || 0) > 0 && !this.hasAchievedGoal(p))
        .reduce((sum, p) => sum + (Math.floor((p.value || 0) / this.dailyTarget) * coinsPerGoal), 0);
    },
    prizePoolSize() {
      return this.totalCoinsIn;
    },
    prizePerWinner() {
      if (this.achieversCount === 0) return 0;
      return Math.round(this.prizePoolSize / this.achieversCount);
    },

    // Effectiveness
    avgScore() {
      if (this.activeParticipants === 0) return 0;
      const total = this.leaderboard.reduce((sum, p) => sum + (p.value || 0), 0);
      return Math.round(total / this.activeParticipants);
    },
    achievementRateColor() {
      if (this.achievementRate >= 50) return "eff-good";
      if (this.achievementRate >= 25) return "eff-warning";
      return "eff-poor";
    },

    // Duration
    durationProgress() {
      if (!this.campaign || !this.campaign.timing) return 0;
      const timing = this.campaign.timing;
      if (!timing.startDate || !timing.endDate) return 0;

      const startDate = this.parseDate(timing.startDate);
      const endDate = this.parseDate(timing.endDate);
      const now = new Date();

      if (!startDate || !endDate) return 0;
      if (now < startDate) return 0;
      if (now >= endDate) return 100;

      const totalDuration = endDate.getTime() - startDate.getTime();
      const elapsed = now.getTime() - startDate.getTime();
      return Math.round((elapsed / totalDuration) * 100);
    },
    durationLabel() {
      if (!this.campaign || !this.campaign.timing) return "";
      const timing = this.campaign.timing;
      if (!timing.startDate || !timing.endDate) return "";

      const startDate = this.parseDate(timing.startDate);
      const endDate = this.parseDate(timing.endDate);
      const now = new Date();

      if (!startDate || !endDate) return "";

      if (now < startDate) {
        const diffTime = startDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) return "Starts tomorrow";
        return `Starts in ${diffDays} days`;
      }

      if (now >= endDate) return "Completed";

      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 1) return "Last day";
      return `${diffDays} days left`;
    },
  },
  watch: {
    visible(val) {
      if (val && this.campaign && this.campaign.id) {
        this.fetchSummary();
      }
    },
  },
  methods: {
    async fetchSummary() {
      if (!this.campaign || !this.campaign.id) return;

      this.loading = true;
      try {
        if (this.isUpcoming) {
          this.signups = await this.$store.dispatch("happilyvillageUniversity/fetchCampaignSignups", this.campaign.id);
        } else {
          const cached = this.$store.getters["happilyvillageUniversity/getCampaignSummary"](this.campaign.id);
          if (!cached) {
            await this.$store.dispatch("happilyvillageUniversity/fetchCampaignSummary", this.campaign.id);
          }
        }
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.signups = [];
      this.$emit("close");
    },
    openLeaderboard() {
      this.$emit("close");
      this.$emit("open-leaderboard", this.campaign);
    },
    formatValue(value) {
      if (value === undefined || value === null) return "0";
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return value;
    },
    formatStatus(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatDateShort(date) {
      if (!date) return "";
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    },
    hasAchievedGoal(participant) {
      if (!this.individualTarget || !participant) return false;
      const value = participant.value || 0;
      return value >= this.individualTarget;
    },
    parseDate(dateValue) {
      if (!dateValue) return null;
      if (dateValue.toDate) return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      if (typeof dateValue === "string") return new Date(dateValue);
      return null;
    },
    getCoinsEarned(participant) {
      if (!participant || !this.rewardConfig) return 0;

      if (participant.coins_earned !== undefined) {
        return participant.coins_earned;
      }

      const reward = this.rewardConfig;

      if (reward.type === "instant") {
        return participant.total_coins || 0;
      } else if (reward.type === "charged") {
        if (this.hasAchievedGoal(participant)) {
          return participant.charged_coins || participant.total_coins || 0;
        }
        return 0;
      } else if (reward.type === "prize_pool") {
        if (this.hasAchievedGoal(participant)) {
          return participant.prize_share || participant.total_coins || 0;
        }
        return 0;
      }

      return participant.total_coins || 0;
    },
  },
};
</script>

<style scoped>
.summary-modal >>> .el-dialog__header {
  display: none;
}

.summary-modal >>> .el-dialog__body {
  padding: 0;
}

.summary-modal >>> .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.summary-container {
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
}

/* Header */
.summary-header {
  background: #6C30CC;
  padding: 20px 24px;
  color: white;
}

.campaign-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: white;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.status-upcoming {
  background: rgba(255, 255, 255, 0.25);
  color: white;
}

.status-ended {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
}

.status-archived {
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.6);
}

.meta-divider {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.meta-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

/* Progress Section */
.progress-section {
  margin-top: 0;
}

.progress-bar-container {
  height: 4px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: white;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-label {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

/* Loading State */
.loading-state {
  padding: 60px 24px;
  display: flex;
  justify-content: center;
}

/* Empty State */
.empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.empty-subtext {
  font-size: 13px;
  color: #9CA3AF;
}

/* Content area */
.summary-content {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 340px);
  padding: 20px 24px;
}

/* Sections */
.section {
  margin-bottom: 24px;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

/* Metric Grid */
.metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.metric-card {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 14px 16px;
}

.metric-label {
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
}

.metric-sub {
  font-size: 11px;
  color: #9CA3AF;
  margin-top: 2px;
}

.coin-inline {
  background-image: url("~@/assets/images/ic_coin.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: inline-block;
  flex-shrink: 0;
}

/* Investment Hero */
.investment-hero {
  background: #1E1636;
  border-radius: 10px;
  padding: 18px 20px;
  color: white;
  margin-bottom: 12px;
}

.investment-hero-top {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.investment-amount {
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
}

.investment-label {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.7;
}

.investment-meta {
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.5;
}

/* Coin Flow Detail */
.coin-flow {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.coin-flow-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.coin-flow-row + .coin-flow-row {
  margin-top: 2px;
}

.coin-flow-label {
  font-size: 12px;
  opacity: 0.5;
}

.coin-flow-value {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 4px;
}

.coin-flow-detail {
  font-weight: 400;
  opacity: 0.6;
}

.coin-flow-returned {
  opacity: 0.5;
}

/* Impact Grid */
.impact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 0;
}

.impact-card {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px 16px;
}

.impact-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.impact-value-good {
  color: #059669;
}

.impact-label {
  font-size: 11px;
  font-weight: 500;
  color: #6B7280;
  margin-top: 2px;
}

/* Projection */
.projection-notice {
  margin-top: 10px;
  padding: 8px 12px;
  background: #FEF3C7;
  border-radius: 6px;
  font-size: 12px;
  color: #92400E;
}

.projection-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.projection-label {
  font-size: 12px;
  color: #9CA3AF;
}

.projection-value {
  font-size: 13px;
  font-weight: 600;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Participation */
.participation-bar {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;
}

.participation-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.part-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.part-stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.part-stat-label {
  font-size: 11px;
  color: #6B7280;
  margin-top: 2px;
}

.part-stat-divider {
  width: 1px;
  height: 32px;
  background: #E5E7EB;
}

.achievement-progress {
  margin-top: 0;
}

.achievement-bar-container {
  height: 8px;
  background: #F3F4F6;
  border-radius: 4px;
  overflow: hidden;
}

.achievement-bar-fill {
  height: 100%;
  background: #059669;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.achievement-label {
  margin-top: 6px;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}

/* Reward Details */
.reward-details {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
}

.reward-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #F3F4F6;
}

.reward-row:last-child {
  border-bottom: none;
}

.reward-row-total {
  background: #FAFBFC;
}

.reward-row-label {
  font-size: 13px;
  color: #374151;
}

.reward-row-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 4px;
}

.reward-positive {
  color: #059669;
}

.reward-neutral {
  color: #6B7280;
  font-style: italic;
}

/* Effectiveness */
.effectiveness-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.eff-stat {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
}

.eff-stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  display: block;
}

.eff-stat-label {
  font-size: 11px;
  color: #6B7280;
  margin-top: 2px;
  display: block;
}

.eff-good {
  color: #059669;
}

.eff-warning {
  color: #D97706;
}

.eff-poor {
  color: #6B7280;
}

/* Footer */
.summary-footer {
  padding: 12px 20px;
  background: #FAFBFC;
  border-top: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-link {
  font-size: 13px;
  font-weight: 500;
  color: #6C30CC;
  cursor: pointer;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #5A28A8;
  text-decoration: underline;
}

/* Scrollbar */
.thin-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.thin-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

.thin-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #D1D5DB;
}
</style>
